import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import {
  ExpenditureBudgetJurisdictionFormSchema,
  ExpenditureBudgetJurisdictionsModel,
} from '../models';

const ExpenditureBudgetJurisdictionsModelElement =
  ExpenditureBudgetJurisdictionsModel.element;

export const useExpenditureBudgetJurisdictions = <
  T = Array<z.infer<typeof ExpenditureBudgetJurisdictionsModelElement>>,
>({
  select,
  params,
}: {
  select?: (data: z.infer<typeof ExpenditureBudgetJurisdictionsModel>) => T;
  params?: { showMigrated?: true };
}) => {
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: ['expenditureBudgetJurisdictions'],
    queryFn: async () => {
      const data = await axiosGet('/api/expenditure-budget-jurisdictions/', {
        ...restParams,
        ...(!showMigrated && { isMigrated: false }),
      });
      return ExpenditureBudgetJurisdictionsModel.parse(data);
    },
    select,
  });
};

export const useExpenditureBudgetJurisdictionOptions = (props?: {
  params?: { showMigrated?: true };
}) => {
  const { params } = props ?? {};
  return useExpenditureBudgetJurisdictions({
    params,
    select: (data) =>
      data.map((d) => ({
        value: d.id,
        label: d.name,
      })),
  });
};

type FormValues = z.infer<typeof ExpenditureBudgetJurisdictionFormSchema>;

export const useCreateExpenditureBudgetJurisdiction = () => {
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, ...payload } =
        ExpenditureBudgetJurisdictionFormSchema.parse(values);
      return await axiosPost('/api/expenditure-budget-jurisdictions/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useUpdateExpenditureBudgetJurisdiction = () => {
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, ...payload } =
        ExpenditureBudgetJurisdictionFormSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
      return await axiosPatch(`/api/expenditure-budget-jurisdictions/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useDeleteExpenditureBudgetJurisdiction = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(
        `/api/expenditure-budget-jurisdictions/${id}/`,
      );
      await invalidate(['expenditureBudgetJurisdictions']);
      return res;
    },
  });
};
